import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { api } from '../api.js';

function emptyForm() {
  return { name: '', addressFull: '', phone: '', fax: '', payableEmail: '', taxTPS: '', taxTVQ: '', active: true };
}

export default function VendorsAdmin() {
  const { t } = useTranslation();
  const [vendors, setVendors] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());

  const reload = () => {
    api.get('/api/vendors').then((res) => setVendors(res.data.vendors)).catch((err) => toast.error(err.message));
  };
  useEffect(() => { reload(); }, []);

  const startNew = () => { setEditing('new'); setForm(emptyForm()); };
  const startEdit = (v) => {
    setEditing(v);
    setForm({
      name: v.name,
      addressFull: v.addressFull || '', phone: v.phone || '', fax: v.fax || '',
      payableEmail: v.payableEmail || '', taxTPS: v.taxTPS || '', taxTVQ: v.taxTVQ || '',
      active: v.active,
    });
  };
  const cancel = () => setEditing(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        addressFull: form.addressFull || null, phone: form.phone || null, fax: form.fax || null,
        payableEmail: form.payableEmail || null, taxTPS: form.taxTPS || null, taxTVQ: form.taxTVQ || null,
      };
      if (editing === 'new') {
        await api.post('/api/vendors', payload);
        toast.success(t('vendors.created'));
      } else {
        await api.put(`/api/vendors/${editing.id}`, payload);
        toast.success(t('vendors.updated'));
      }
      cancel(); reload();
    } catch (err) { toast.error(err.message); }
  };

  const remove = async (v) => {
    if (!window.confirm(t('vendors.confirmDelete'))) return;
    try {
      await api.del(`/api/vendors/${v.id}`);
      toast.success(t('vendors.deleted'));
      reload();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">{t('vendors.title')}</h1>
        <button onClick={startNew} className="btn-primary">{t('vendors.new')}</button>
      </div>
      {editing && (
        <form onSubmit={submit} className="card p-4 mb-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><label className="label">{t('vendors.form.name')}</label>
              <input required className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="col-span-2"><label className="label">{t('vendors.form.addressFull')}</label>
              <input className="input" value={form.addressFull} onChange={(e) => setForm({ ...form, addressFull: e.target.value })} /></div>
            <div><label className="label">{t('vendors.form.phone')}</label>
              <input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
            <div><label className="label">{t('vendors.form.fax')}</label>
              <input className="input" value={form.fax} onChange={(e) => setForm({ ...form, fax: e.target.value })} /></div>
            <div className="col-span-2"><label className="label">{t('vendors.form.payableEmail')}</label>
              <input type="email" className="input" value={form.payableEmail} onChange={(e) => setForm({ ...form, payableEmail: e.target.value })} /></div>
            <div><label className="label">{t('vendors.form.taxTPS')}</label>
              <input className="input" value={form.taxTPS} onChange={(e) => setForm({ ...form, taxTPS: e.target.value })} /></div>
            <div><label className="label">{t('vendors.form.taxTVQ')}</label>
              <input className="input" value={form.taxTVQ} onChange={(e) => setForm({ ...form, taxTVQ: e.target.value })} /></div>
            <label className="col-span-2 text-xs text-zinc-400 flex items-center gap-2">
              <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
              {t('common.active')}
            </label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={cancel} className="btn-ghost">{t('common.cancel')}</button>
            <button type="submit" className="btn-primary">{t('common.save')}</button>
          </div>
        </form>
      )}
      <ul className="space-y-2">
        {vendors.map((v) => (
          <li key={v.id} className="card p-3 flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-medium">{v.name}</span>
                {!v.active && <span className="text-[10px] uppercase text-zinc-600">inactive</span>}
              </div>
              <div className="text-xs text-zinc-500 mt-0.5 truncate">
                {[v.addressFull, v.payableEmail, [v.taxTPS, v.taxTVQ].filter(Boolean).join(' / ')].filter(Boolean).join(' · ') || '—'}
              </div>
            </div>
            <div className="flex gap-1 text-xs">
              <button onClick={() => startEdit(v)} className="btn-ghost text-xs">{t('common.edit')}</button>
              <button onClick={() => remove(v)} className="text-rose-400 hover:text-rose-300 px-2">{t('common.delete')}</button>
            </div>
          </li>
        ))}
        {vendors.length === 0 && <li className="text-sm text-zinc-500 py-6 text-center">{t('vendors.empty')}</li>}
      </ul>
    </div>
  );
}
